import { Axios } from '@utils/api/axios-instance.ts';
import { BaseError, BaseResponse } from '@utils/types/shared/base.ts';
import { AxiosError } from 'axios';
import { DELETE_URL } from '@utils/api/endpoints.ts';
import { useMutation, useQueryClient } from 'react-query';
import { CUSTOM_MODEL_QUERY_KEYS } from './query-keys';

export interface DeleteCustomModelPayload {
  modelId: number;
  /** Confirms the caller means to destroy the model's contributed training
   * clips. The API refuses with 409 unacknowledged-contributions without it,
   * because those clips were cut from sessions by hand and the sources age out
   * on the organisation's retention window. */
  acknowledgeContributions?: boolean;
}

async function deleteCustomModel(
  payload: DeleteCustomModelPayload
): Promise<BaseResponse<unknown>> {
  const { modelId, acknowledgeContributions } = payload;
  const url = `${DELETE_URL(modelId.toString()).deleteCustomModel}${
    acknowledgeContributions ? '?acknowledge_contributions=true' : ''
  }`;
  const { data } = await Axios.delete(url);
  return data;
}

export function useDeleteCustomModel() {
  const queryClient = useQueryClient();

  return useMutation<
    BaseResponse<unknown>,
    AxiosError<BaseError>,
    DeleteCustomModelPayload
  >(deleteCustomModel, {
    onSuccess: (_data, { modelId }) => {
      queryClient.removeQueries(CUSTOM_MODEL_QUERY_KEYS.detail(modelId));
      queryClient.removeQueries(CUSTOM_MODEL_QUERY_KEYS.data(modelId));
      queryClient.invalidateQueries(CUSTOM_MODEL_QUERY_KEYS.all);
    },
    useErrorBoundary: false
  });
}
